import { createEvent, createStore, restore, sample } from 'effector'
import { createGate } from 'effector-react'
import { fx } from 'src/libs'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { buildQuery } from 'src/pages/worldCup2026/utils'
import {
  DisableTaskTypeRequest,
  ModerationTaskRequest,
  Task,
  TasksResponse,
  TasksTypesResponse,
  TaskType,
} from './types'

import {
  BASE_URL,
  PAGE_SIZE,
  TASKS_TYPES_NAMES,
} from 'src/pages/worldCup2026/const'
import { ListRequest } from 'src/pages/worldCup2026/types'

export const TaskListGate = createGate()

export const onChangeTaskList = createEvent<Partial<ListRequest>>()
export const disableTaskTypeEvent = createEvent<DisableTaskTypeRequest>()
export const submitModerationTask = createEvent<
  ModerationTaskRequest & { id: string }
>()

export const getTasksFx = fx<ListRequest, TasksResponse>({
  method: 'GET',
  url: `${BASE_URL}participant_task`,
})

export const getTaskTypesFx = fx<void | any, TasksTypesResponse>({
  method: 'GET',
  url: `${BASE_URL}task`,
})

export const disableTaskTypeFx = fx<DisableTaskTypeRequest, void>({
  method: 'PUT',
  url: `${BASE_URL}task/`,
})

export const moderationTaskFx = fx<ModerationTaskRequest, void>({
  method: 'PUT',
  url: `${BASE_URL}participant_task/`,
})

export const $tasks = createStore<Task[]>([]).on(
  getTasksFx.doneData,
  (_, res) => res.body.items,
)

export const $totalCountOfTasks = restore(
  getTasksFx.doneData.map((a) => a.body.total),
  0,
)

export const $tasksTypes = createStore<TaskType[]>([])
  .on(getTaskTypesFx.doneData, (_, res) => res.body.items)
  .on(getTaskTypesFx.fail, () =>
    Object.entries(TASKS_TYPES_NAMES).map(([key, value]) => ({
      taskType: key,
      disabledAt: null,
    })),
  )

export const $tasksParams = createStore<ListRequest>({
  page: 1,
  limit: PAGE_SIZE,
  orderBy: '',
  orderDirection: '',
  filters: { status: 'check' },
}).on(onChangeTaskList, (state, payload) => ({ ...state, ...payload }))

sample({
  clock: [TaskListGate.open, disableTaskTypeFx.done],
  fn: () => ({}),
  target: getTaskTypesFx,
})

sample({
  clock: TaskListGate.open,
  source: $tasksParams,
  fn: buildQuery,
  target: getTasksFx,
})

sample({
  clock: onChangeTaskList,
  fn: buildQuery,
  target: getTasksFx,
})

sample({
  clock: disableTaskTypeEvent,
  fn: ({ id }) => ({ url: `${BASE_URL}task/${id}` }),
  target: disableTaskTypeFx,
})

sample({
  clock: submitModerationTask,
  fn: ({ id, status, declineReason }) => ({
    url: `${BASE_URL}participant_task/${id}`,
    body: {
      status,
      declineReason,
    },
  }),
  target: moderationTaskFx,
})

sample({
  clock: moderationTaskFx.done,
  source: $tasksParams,
  fn: buildQuery,
  target: getTasksFx,
})

sample({
  clock: [
    getTasksFx.failData,
    getTaskTypesFx.failData,
    disableTaskTypeFx.failData,
    moderationTaskFx.failData,
  ],
  fn: (error): NotificationPayload => ({
    type: 'error',
    message: 'Request failed',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})

sample({
  clock: disableTaskTypeFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Task disabled',
  }),
  target: showNotification,
})

sample({
  clock: moderationTaskFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Participant task status updated',
  }),
  target: showNotification,
})
